/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import EmployeeNameSearch from '../../../../components/EmployeeNameSearch';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';
import { QtyInput as BaseQtyInput } from '../../../../styles/ui/Form';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { DataTable } from '../../../../styles/ui/Table';
import { FlagRow, FooterNote, ModalIntro } from './modalKit';
import { productName, routeLabel } from '../utils/format';

// =============================================================================
// EXPEDIÇÃO de remessa (POST /transfer-orders/:id/shipments).
//
// É o passo que move estoque de verdade: sai da origem e fica em trânsito. Por
// isso o botão de confirmação é azul (FC7 §G.1 — escreve no banco).
//
// O responsável pelo transporte é um FUNCIONÁRIO (RH) e é OPCIONAL desde o
// handoff 0108 (2026-09-11): quem transporta nem sempre tem login, e a remessa
// pode sair sem responsável designado. Só a seleção na lista vale como valor —
// texto digitado sem escolher ninguém é limpo no blur (prop `required` do
// autocomplete), para não parecer preenchido e sair vazio.
//
// Remessa parcial é normal: cada linha vem pré-preenchida com o saldo pendente
// (`remaining_qtd`) e o operador reduz o que não vai. Item com saldo zerado não
// entra na lista — expedir acima do pedido é 422 `transfer_over_shipment`.
// =============================================================================

const InlineError = styled.small`
   display: block;
   margin-top: 6px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ theme }) => theme.colors.danger};
`;

const QtyInput = styled(BaseQtyInput)`
   max-width: 110px;
`;

const integerRegex = /^[0-9]+$/;

export default function DispatchModal({
   isOpen,
   order,
   isSubmitting = false,
   fieldErrors = {},
   itemErrors = {},
   onCancel,
   onConfirm,
}) {
   // O autocomplete de funcionário é controlado por DOIS estados: o texto
   // visível e o id, que só existe depois de uma seleção de verdade.
   const [responsibleEmployeeId, setResponsibleEmployeeId] = useState('');
   const [responsibleQuery, setResponsibleQuery] = useState('');
   const [notes, setNotes] = useState('');
   const [quantities, setQuantities] = useState({});
   const [touched, setTouched] = useState(false);

   // Só itens com saldo pendente são expedíveis.
   const pendingItems = useMemo(
      () => (order?.items ?? []).filter((item) => Number(item.remaining_qtd) > 0),
      [order]
   );

   // Reabrir o modal recomeça limpo, com cada linha no saldo pendente.
   useEffect(() => {
      if (!isOpen) return;
      setResponsibleEmployeeId('');
      setResponsibleQuery('');
      setNotes('');
      setTouched(false);
      setQuantities(
         Object.fromEntries(
            pendingItems.map((item) => [
               item.prod_id,
               String(item.remaining_qtd),
            ])
         )
      );
   }, [isOpen, pendingItems]);

   const handleQtyChange = (prodId, rawValue) => {
      if (rawValue !== '' && !integerRegex.test(rawValue)) return;
      setQuantities((prev) => ({ ...prev, [prodId]: rawValue }));
   };

   const selectedItems = pendingItems
      .map((item) => ({
         prodId: item.prod_id,
         shippedQtd: Number(quantities[item.prod_id] ?? 0),
         remaining: Number(item.remaining_qtd),
      }))
      .filter((item) => item.shippedQtd > 0);

   // Saldo de referência da linha: o 422 estruturado (handoff 0107 §5.2) traz
   // o `remaining_qtd` ATUAL do servidor — sob concorrência ele vale mais que
   // o que a tela carregou, e o operador corrige sem fechar o modal.
   const capOf = (item) =>
      Number(itemErrors[item.prod_id]?.remaining_qtd ?? item.remaining_qtd);

   const hasOverShipment = pendingItems.some((item) => {
      const qty = Number(quantities[item.prod_id] ?? 0);
      return qty > capOf(item);
   });

   const totalToShip = selectedItems.reduce(
      (acc, item) => acc + item.shippedQtd,
      0
   );

   const canConfirm =
      selectedItems.length > 0 && !hasOverShipment && !isSubmitting;

   const handleConfirm = () => {
      setTouched(true);
      if (!canConfirm) return;
      onConfirm({
         responsibleEmployeeId,
         notes: notes.trim(),
         items: selectedItems.map(({ prodId, shippedQtd }) => ({
            prodId,
            shippedQtd,
         })),
      });
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Expedir remessa"
         subtitle={
            order
               ? `Transferência #${order.id} · ${routeLabel(order)}`
               : null
         }
         onClose={isSubmitting ? undefined : onCancel}
         maxWidth="820px"
         footer={
            <>
               {/* Nota à esquerda: contagem da remessa, ou o motivo do
                   bloqueio quando alguma linha passa do pendente. */}
               {hasOverShipment ? (
                  <FooterNote $warn>
                     Há linha acima do saldo pendente — o servidor recusaria a
                     expedição.
                  </FooterNote>
               ) : (
                  <FooterNote>
                     {totalToShip > 0
                        ? `${totalToShip} un nesta remessa`
                        : 'Informe as quantidades a expedir.'}
                  </FooterNote>
               )}
               <GhostButton
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
               >
                  {isSubmitting ? 'Expedindo…' : 'Expedir remessa'}
               </PrimaryButton>
            </>
         }
      >
         <ModalIntro $tone="info">
            Ao confirmar, o estoque <strong>sai da origem</strong> e fica em
            trânsito até a conferência no destino. Envio parcial é permitido —
            ajuste as quantidades e expeça o restante em outra remessa.
         </ModalIntro>

         <FormGrid $cols={12} $colsMobile={12}>
            <Field $col={6} $colMobile={12}>
               <label htmlFor="dispatch-responsible">
                  Responsável pelo transporte (opcional)
               </label>
               <EmployeeNameSearch
                  value={responsibleQuery}
                  onChange={setResponsibleQuery}
                  onSelect={(employee) => {
                     setResponsibleEmployeeId(
                        employee ? String(employee.id) : ''
                     );
                     setResponsibleQuery(employee?.full_name ?? '');
                  }}
                  required
                  placeholder="Busque o funcionário que leva a remessa..."
                  disabled={isSubmitting}
               />
               {fieldErrors.responsible_employee_id && (
                  <InlineError>
                     {fieldErrors.responsible_employee_id}
                  </InlineError>
               )}
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="dispatch-notes">Observações</label>
               <input
                  id="dispatch-notes"
                  type="text"
                  value={notes}
                  disabled={isSubmitting}
                  placeholder="Opcional — ex.: veículo, lacre, rota"
                  onChange={(event) => setNotes(event.target.value)}
               />
               {fieldErrors.notes && (
                  <InlineError>{fieldErrors.notes}</InlineError>
               )}
            </Field>
         </FormGrid>

         <TableWrapper style={{ marginTop: 16 }} $minWidth="560px">
            <DataTable>
               <thead>
                  <tr>
                     <th className="center">SKU</th>
                     <th>Produto</th>
                     <th className="num">Pendente</th>
                     <th className="center">A expedir</th>
                  </tr>
               </thead>
               <tbody>
                  {pendingItems.map((item) => {
                     const qty = Number(quantities[item.prod_id] ?? 0);
                     const cap = capOf(item);
                     const serverError = itemErrors[item.prod_id];
                     const isOver = qty > cap;
                     return (
                        <FlagRow
                           key={item.prod_id}
                           $flag={isOver ? 'invalid' : null}
                        >
                           <td className="center">{item.prod_sku ?? '—'}</td>
                           <td>{productName(item)}</td>
                           <td
                              className="num"
                              title={
                                 serverError
                                    ? 'Saldo atualizado pelo servidor após o conflito'
                                    : undefined
                              }
                           >
                              {cap}
                           </td>
                           <td className="center">
                              <QtyInput
                                 type="text"
                                 inputMode="numeric"
                                 aria-label={`Quantidade a expedir de ${
                                    item.prod_name ?? item.prod_id
                                 }`}
                                 aria-invalid={isOver || undefined}
                                 $invalid={isOver}
                                 value={quantities[item.prod_id] ?? ''}
                                 disabled={isSubmitting}
                                 onChange={(event) =>
                                    handleQtyChange(
                                       item.prod_id,
                                       event.target.value
                                    )
                                 }
                              />
                              {isOver && (
                                 <InlineError>
                                    {serverError?.message ??
                                       'Acima do saldo pendente.'}
                                 </InlineError>
                              )}
                           </td>
                        </FlagRow>
                     );
                  })}
               </tbody>
            </DataTable>
         </TableWrapper>

         {touched && selectedItems.length === 0 && (
            <InlineError>
               Informe a quantidade de pelo menos um item para expedir.
            </InlineError>
         )}
      </Modal>
   );
}

DispatchModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   order: PropTypes.object,
   isSubmitting: PropTypes.bool,
   // eslint-disable-next-line react/forbid-prop-types
   fieldErrors: PropTypes.object,
   // { [prod_id]: violação estruturada } do 422 (handoff 0107 §5.2).
   // eslint-disable-next-line react/forbid-prop-types
   itemErrors: PropTypes.object,
   onCancel: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
};
